import { Box } from '@mui/material';
import { useState } from 'react';

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '0.0.0.0'];
const STAGING_HOSTNAME_PATTERN = /bkrr\.net$/i;
const STORAGE_KEY = 'env_indicator_collapsed';
const BRAND_GRADIENT = 'rgba(255, 255, 255, 0.2)';

const readCollapsedState = () => {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return stored === 'true';
    } catch (error) {
        return false;
    }
};

const writeCollapsedState = (value) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, String(value));
    } catch (error) {
        // storage unavailable (private mode / blocked cookies)
    }
};

export const getEnvironment = () => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

    if (LOCAL_HOSTNAMES.includes(hostname) || hostname.endsWith('.local')) {
        return 'local';
    }
    if (STAGING_HOSTNAME_PATTERN.test(hostname) || process.env.REACT_APP_MODE !== 'production') {
        return 'staging';
    }
    return 'production';
};

const EnvironmentIndicator = () => {
    const environment = getEnvironment();
    const [isCollapsed, setIsCollapsed] = useState(false);

    if (environment === 'production') return null;

    const label = environment === 'local' ? 'LOCAL' : 'STAGING';
    const portal = process.env.REACT_APP_PORTAL ? ` - ${String(process.env.REACT_APP_PORTAL).toUpperCase()}` : '';
    const version = process.env.REACT_APP_BASE_DEV_VERSION || process.env.REACT_APP_BASE_PROD_VERSION;
    const tooltip = [
        `${label} environment${portal}`,
        version ? `Version: ${version}` : null,
        `API: ${process.env.REACT_APP_BASE_URL}`,
        isCollapsed ? 'Click to expand' : 'Click to minimise'
    ]
        .filter(Boolean)
        .join('\n');

    const toggle = () => {
        const next = !isCollapsed;
        setIsCollapsed(next);
        writeCollapsedState(next);
    };

    return (
        <>
            <style>{`
                [data-env-badge] {
                    position: fixed;
                    top: 0;
                    left: 50%;
                    transform: translateX(-50%);
                    z-index: 999999;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #1F2937;
                    border-radius: 0 0 8px 8px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
                    font-size: 10px;
                    font-weight: 700;
                    line-height: 1.3;
                    letter-spacing: 0.1em;
                    font-family: inherit;
                    white-space: nowrap;
                    cursor: pointer;
                    user-select: none;
                    width: auto;
                    min-width: max-content;
                    background: #F4F0FF;
                    border: 1px solid #E9D5FF;
                    padding: 4px 12px 5px;
                }

                [data-env-badge][data-collapsed="true"] {
                    padding: 0 20px 3px;
                }

                [data-env-dot] {
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background-color: #7852F8;
                    display: inline-block;
                }
            `}</style>

            <Box
                aria-hidden="true"
                sx={{
                    position: 'fixed',
                    inset: 0,
                    pointerEvents: 'none',
                    zIndex: 999998
                }}
            />

            <Box
                data-env-badge
                data-collapsed={isCollapsed}
                onClick={toggle}
                title={tooltip}
                aria-label={`${label} environment`}
            >
                {!isCollapsed && (
                    <>
                        <Box component="span" data-env-dot />
                        {`${label} ENVIRONMENT${portal}`}
                    </>
                )}
            </Box>
        </>
    );
};

export default EnvironmentIndicator;
